export type CompassTargetKind = 'pickup' | 'dropoff';

// Kept in sync with --am-gold / --am-cyan in index.html design tokens.
const COLOR_BY_KIND: Record<CompassTargetKind, string> = {
  pickup: '#ffcc33',
  dropoff: '#33ccff',
};

const LABEL_BY_KIND: Record<CompassTargetKind, string> = {
  pickup: 'PICKUP',
  dropoff: 'DROP-OFF',
};

export class Compass {
  private root: HTMLElement;
  private dial: HTMLElement;
  private arrow: HTMLElement;
  private label: HTMLElement;
  private distance: HTMLElement;

  constructor() {
    const existing = document.getElementById('mission-compass');
    if (existing) existing.remove();

    this.root = document.createElement('div');
    this.root.id = 'mission-compass';
    this.root.style.cssText = [
      'position:fixed', 'top:72px', 'left:50%',
      'transform:translateX(-50%)',
      'display:none',
      'flex-direction:column', 'align-items:center', 'gap:5px',
      'z-index:100', 'pointer-events:none',
      'font-family:var(--am-font)',
    ].join(';');

    this.label = document.createElement('div');
    this.label.style.cssText = [
      'padding:3px 12px', 'border-radius:var(--am-radius-pill)',
      'background:var(--am-glass-strong)',
      'backdrop-filter:blur(8px)', '-webkit-backdrop-filter:blur(8px)',
      'color:#fff', 'font-family:var(--am-font)',
      'font-size:0.62rem', 'font-weight:700',
      'letter-spacing:0.14em', 'text-transform:uppercase',
    ].join(';');

    this.dial = document.createElement('div');
    this.dial.style.cssText = [
      'width:52px', 'height:52px',
      'transform-origin:50% 50%',
    ].join(';');

    this.arrow = document.createElement('div');
    this.arrow.style.cssText = [
      'width:0', 'height:0',
      'border-left:14px solid transparent',
      'border-right:14px solid transparent',
      'border-bottom:30px solid #ffcc33',
      'margin:4px auto 0',
      'filter:drop-shadow(0 2px 4px rgba(0,0,0,0.55))',
      'transition:border-bottom-color 0.2s ease',
    ].join(';');
    this.dial.appendChild(this.arrow);

    this.distance = document.createElement('div');
    this.distance.style.cssText = [
      'padding:2px 10px', 'border-radius:var(--am-radius-pill)',
      'background:var(--am-glass)',
      'backdrop-filter:blur(8px)', '-webkit-backdrop-filter:blur(8px)',
      'color:#fff', 'font-family:var(--am-font)',
      'font-size:0.66rem', 'font-weight:600',
      'font-variant-numeric:tabular-nums',
      'letter-spacing:0.04em',
    ].join(';');

    this.root.appendChild(this.label);
    this.root.appendChild(this.dial);
    this.root.appendChild(this.distance);
    document.body.appendChild(this.root);
  }

  /**
   * @param target Current pickup/dropoff position in world space (curbX/curbZ).
   * @param cameraYaw ThirdPersonCamera.getYaw() — position angle of the camera.
   * @param kind Whether we're pointing at a pickup or a drop-off.
   */
  update(
    playerX: number,
    playerZ: number,
    target: { x: number; z: number } | null,
    cameraYaw: number,
    kind: CompassTargetKind = 'pickup',
  ) {
    if (!target) {
      this.root.style.display = 'none';
      return;
    }

    // Keep the compass visible at all times while a mission is active — the
    // previous "hide when on-screen" behaviour left players unsure whether
    // the target was ahead of them or behind.
    this.root.style.display = 'flex';

    const dx = target.x - playerX;
    const dz = target.z - playerZ;
    const worldAngle = Math.atan2(dx, -dz);
    // ThirdPersonCamera.yaw is the camera's POSITION angle, not its facing
    // direction. Its facing = -yaw in this convention, so screen bearing adds.
    let screenAngle = worldAngle + cameraYaw;
    while (screenAngle > Math.PI) screenAngle -= 2 * Math.PI;
    while (screenAngle < -Math.PI) screenAngle += 2 * Math.PI;
    this.dial.style.transform = `rotate(${screenAngle}rad)`;

    const color = COLOR_BY_KIND[kind];
    this.arrow.style.borderBottomColor = color;
    this.label.textContent = LABEL_BY_KIND[kind];
    this.label.style.color = color;

    const distance = Math.hypot(dx, dz);
    this.distance.textContent = `${distance.toFixed(0)} m`;
  }

  destroy() {
    this.root.remove();
  }
}
